import {
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { msalInstance } from '../index';
import { protectedResources } from '../authConfig';
import { appEventEmitter, AUTH_ERROR_EVENT } from './eventEmitter';
import { clearFmsToken, getFmsToken } from '../auth/fmsToken';

const resolveActiveAccount = (instance: IPublicClientApplication) => {
  const active = instance.getActiveAccount();
  if (active) return active;
  const all = instance.getAllAccounts();
  if (all.length === 1) {
    instance.setActiveAccount(all[0]);
    return all[0];
  }
  return null;
};

export async function getAccessToken(
  instance: IPublicClientApplication
): Promise<string> {
  const account = resolveActiveAccount(instance);
  if (!account) return '';

  const request = {
    ...protectedResources.assetApi,
    account: account,
  };
  try {
    const response = await instance.acquireTokenSilent(request);
    if (response) {
      return response.accessToken;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect(request);
      return '';
    }
    appEventEmitter.emit(AUTH_ERROR_EVENT, 'Error acquiring token silently.');
    return '';
  }
  return '';
}

const isBootstrapUrl = (url: string): boolean => url.includes('/a/authorize');

export const getAuthHeader = async (url: string): Promise<Headers> => {
  const headers = new Headers();
  const accessToken = isBootstrapUrl(url)
    ? await getAccessToken(msalInstance)
    : await getFmsToken();
  headers.append('Authorization', `Bearer ${accessToken}`);
  return headers;
};

export type SignOutReason = 'authFailed' | 'sessionExpired';

export const signOut = async (
  instance: IPublicClientApplication,
  reason?: SignOutReason
): Promise<void> => {
  clearFmsToken();
  try {
    const activeAccount = instance.getActiveAccount();
    const staleAccounts = instance
      .getAllAccounts()
      .filter(
        (account) => account.homeAccountId !== activeAccount?.homeAccountId
      );
    await Promise.all(
      staleAccounts.map((account) =>
        instance.clearCache({ account }).catch(() => undefined)
      )
    );
  } catch {
    /* */
  }
  try {
    const postLogoutRedirectUri = reason
      ? `${window.location.origin}/?reason=${reason}`
      : undefined;
    await instance.logoutRedirect({ postLogoutRedirectUri });
  } catch (e) {
    console.error('MSAL logoutRedirect failed', e);
  }
};
